import { type AxiosRequestConfig, type AxiosResponse, isAxiosError } from 'axios'
import axios from '@/vue/helpers/axios'

interface Params {
    [key: string]: string | number | boolean
}

export const encodeUrl = (url: string, queryParams: Params = {}) => {
    const formattedParamsArray = Object.keys(queryParams).map((key) => {
        return `${key}=${encodeURIComponent(queryParams[key])}`
    })
    const formattedParamsString = formattedParamsArray.join('&')
    return url + '?' + formattedParamsString
}

/**
 * @deprecated Use `fetchApiData` instead and type the response accordingly.
 *
 * Returns a promise that resolves to an AxiosResponse object, or an error response that looks enough like an
 * AxiosResponse to be used in the way it is used to check the status and show the error message.
 */
export default async (
    url: string,
    queryParams: Params = {},
    config: AxiosRequestConfig = {},
): Promise<AxiosResponse<any>> => {
    try {
        const formattedFullUrl = encodeUrl(url, queryParams)
        const response = await axios.get<any>(formattedFullUrl, config)
        return response ?? errorResponse('No response received from the server.')
    } catch (error) {
        let message = 'An error occurred while fetching data. '
        if (isAxiosError(error) && error.response) {
            message += (error.response.data as BaseApiResponse<any>)?.message || error.message
        } else if (error instanceof Error) {
            message += error.message
        }
        // @ts-expect-error baseline
        return errorResponse(message)
    }
}

const errorResponse = (message: string) => ({
    status: 500,
    statusText: message,
    success: false,
    message: `Something went wrong: ${message}.`,
    data: {
        success: false,
        message: `Something went wrong: ${message}.`,
        data: {
            success: false,
            message: `Something went wrong: ${message}.`,
        },
    },
})

export interface BaseApiResponse<T> {
    data: T
    message: string
    success: boolean
}

export interface DataWrapper<T> {
    data: T
}

export interface CommonResponse<T> extends BaseApiResponse<DataWrapper<T>> { }

export interface ApiResponse<T> {
    data: T | null
    error: string | null
}

export async function fetchApiData<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
    try {
        const response: AxiosResponse<T> = await axios.request<T>({
            url, params, ...{
                method: 'GET',
                ...config,
            },
        })
        return { data: response.data, error: null }
    } catch (error) {
        let message = 'An error occurred while fetching data. '
        if (isAxiosError(error) && error.response) {
            message += (error.response.data as BaseApiResponse<T>)?.message || error.message
        } else if (error instanceof Error) {
            message += error.message
        }
        return { error: message, data: null }
    }
}

export async function postApiData<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
    try {
        const response: AxiosResponse<T> = await axios.request<T>({
            url,
            data,
            method: 'POST',
            ...config,
        })
        return { data: response.data, error: null }
    } catch (error) {
        let message = 'An error occurred while posting data. '
        if (isAxiosError(error) && error.response) {
            message += (error.response.data as BaseApiResponse<T>)?.message || error.message
        } else if (error instanceof Error) {
            message += error.message
        }
        return { error: message, data: null }
    }
}
